import { defineComponent, nextTick } from "vue";
import { IStaticTableCustomSettings } from "../../vue-interfaces/i-static-table-settings";

export default defineComponent({
    props: {
        enabled: { type: Boolean, default: true },
        isLoading: { type: Boolean, default: true },
        frontColumnCount: { type: Number, default: 1 },
        backColumnCount: { type: Number, default: 1 },
        tableColumnSettings: { type: [], default: [] },
        columnsAltered: { type: Boolean, default: false },
        columns: { type: Number, default: 1 }
    },

    data(vm) {
        return {
            stickyFront: 0 as number,
            stickyBack: 0 as number,
            lastScrollX: 0 as number,
            eventCells: [] as HTMLElement[],
            wrapper: this.$refs.stickyWrapper as HTMLDivElement,
            deviceScreen: "monitor",
            resizeObserver: null as ResizeObserver,
            newColspan: 1 as number
        };
    },

    created() {
        this.stickyBack = this.backColumnCount;
        this.stickyFront = this.frontColumnCount;
    },

    async mounted() {
        await nextTick();
        this.assignMobileSticky();
        window.addEventListener("resize", this.assignMobileSticky);
        this.initiateStyling();
        this.calculateColspan();
        this.resizeObserver = new ResizeObserver(() => {
            this.calculateColspan();
        });
        if (this.wrapper) this.resizeObserver.observe(this.wrapper);
    },

    async beforeUnmount() {
        window.removeEventListener("resize", this.assignMobileSticky);
        this.wrapper?.removeEventListener("scroll", this.scrollEvent);
        this.eventCells.forEach((tdValue) => {
            tdValue.removeEventListener("shown.bs.dropdown", this.shownDropDownEvent);
            tdValue.removeEventListener("hidden.bs.dropdown", this.hiddenDropDownEvent);
        });
        if (this.resizeObserver && this.wrapper) this.resizeObserver.unobserve(this.wrapper);
    },

    watch: {
        async isLoading(newValue: any) {
            this.initiateStyling();
            this.calculateColspan();
        },

        async watchStickyGroup(newValue: any) {
            this.initiateStyling();
        },

        async columnsAltered(newValue: any) {
            this.initiateStyling();
            this.calculateColspan();
        }
    },

    computed: {
        watchStickyGroup() {
            return JSON.stringify({
                a: this.stickyFront,
                b: this.stickyBack,
                c: this.deviceScreen
            });
        }
    },

    methods: {
        assignMobileSticky() {
            const width = window.innerWidth;
            if (width <= 800 && width >= 500) {
                this.stickyFront = this.frontColumnCount > 2 ? 2 : this.frontColumnCount;
                this.stickyBack = this.backColumnCount > 1 ? 1 : this.backColumnCount;
                this.deviceScreen = "tab";
            } else if (width <= 500) {
                this.stickyFront = this.frontColumnCount > 0 ? 0 : this.frontColumnCount;
                this.stickyBack = this.backColumnCount > 1 ? 1 : this.backColumnCount;
                this.deviceScreen = "mobile";
            } else {
                this.stickyFront = this.frontColumnCount;
                this.stickyBack = this.backColumnCount;
                this.deviceScreen = "monitor";
            }
        },

        addStickyStyle() {
            this.wrapper = this.$refs.stickyWrapper as HTMLDivElement;
            const table = this.wrapper.querySelector("table");
            const columnSettings = this.tableColumnSettings as IStaticTableCustomSettings[];
            this.lastScrollX = this.wrapper.scrollLeft;
            if (!table) return;

            const tableHead = table.querySelectorAll("thead th");
            if (!tableHead) return;

            const numberOfColumns = tableHead.length;
            tableHead.forEach((th, i) => {
                let thValue = th as HTMLElement;
                thValue.classList.add("stickyHead");
                thValue.style.setProperty(
                    "--sticky-custom-width",
                    `${columnSettings.find((x) => x.RowIndex === i + 1)?.Width ?? 200}px`
                );
                return thValue;
            });

            table.querySelectorAll("tbody td").forEach((val) => {
                val.classList.remove("sticky");
            });
            tableHead.forEach((val) => {
                let thValue = val as HTMLElement;
                thValue.classList.remove("sticky");
                thValue.style.left = "inherit";
                thValue.style.zIndex = "501";
            });

            let left = 0;
            let leftTab = 0;
            let leftMobile = 0;
            for (let i = 0; i < this.stickyFront; i++) {
                const tHead = tableHead[i] as HTMLElement;
                if (!tHead) return;

                tHead.classList.add("sticky");
                tHead.style.left = left + "px";
                tHead.style.setProperty("--sticky-mob-left", `${leftMobile}px`);
                tHead.style.setProperty("--sticky-tab-left", `${leftTab}px`);
                tHead.style.setProperty(
                    "--sticky-custom-width",
                    `${columnSettings.find((x) => x.RowIndex === i + 1)?.Width ?? 200}px`
                );

                table.querySelectorAll(`tbody td:nth-child(${i + 1})`).forEach((td, index) => {
                    let tdValue = td as HTMLElement;
                    tdValue.classList.add("sticky");
                    tdValue.style.left = left + "px";
                    tdValue.style.setProperty("--sticky-mob-left", `${leftMobile}px`);
                    tdValue.style.setProperty("--sticky-tab-left", `${leftTab}px`);
                    tdValue.style.setProperty(
                        "--sticky-custom-width",
                        `${columnSettings.find((x) => x.RowIndex === i + 1)?.Width ?? 200}px`
                    );

                    const actionColumn = tdValue.querySelector(".dropdown-menu");
                    if (![0, 1].some((drp) => drp === index) && actionColumn) {
                        actionColumn.classList.add("dropup");
                    }

                    return tdValue;
                });

                table.querySelectorAll(`thead th:nth-child(${i + 1})`).forEach((th) => {
                    let thValue = th as HTMLElement;
                    thValue.style.zIndex = "502";
                    return thValue;
                });

                leftMobile += 50;
                leftTab += 100;
                left += tHead.offsetWidth;
            }

            let right = 0;
            let rightTab = 0;
            let rightMobile = 0;
            for (let i = 0; i < this.stickyBack; i++) {
                const columnIndex = numberOfColumns - 1 - i;
                const tHead = tableHead[columnIndex] as HTMLElement;

                if (!tHead) continue;
                tHead.classList.add("stickyBack");
                tHead.style.right = right + "px";
                tHead.style.setProperty("--sticky-mob-left", `${rightMobile}px`);
                tHead.style.setProperty("--sticky-tab-left", `${rightTab}px`);
                tHead.style.setProperty(
                    "--sticky-custom-width",
                    `${columnSettings.find((x) => x.RowIndex === columnIndex + 1)?.Width ?? 200}px`
                );

                table.querySelectorAll(`tbody td:nth-child(${columnIndex + 1})`).forEach((td, index) => {
                    let tdValue = td as HTMLElement;
                    tdValue.classList.add("sticky");
                    tdValue.style.right = right + "px";
                    tdValue.style.setProperty("--sticky-mob-left", `${leftMobile}px`);
                    tdValue.style.setProperty("--sticky-tab-left", `${leftTab}px`);
                    tdValue.style.setProperty(
                        "--sticky-custom-width",
                        `${columnSettings.find((x) => x.RowIndex === columnIndex + 1)?.Width ?? 200}px`
                    );

                    const actionColumn = tdValue.querySelector(".dropdown-menu") as HTMLElement;
                    if (![0, 1].some((bckdrp) => bckdrp === index) && actionColumn) {
                        actionColumn.classList.add("dropup");
                    }

                    if ([0, 1].some((ev) => ev === index) && actionColumn) {
                        tdValue.addEventListener("shown.bs.dropdown", this.shownDropDownEvent);
                        tdValue.addEventListener("hidden.bs.dropdown", this.hiddenDropDownEvent);
                        this.wrapper.addEventListener("scroll", this.scrollEvent);
                        this.eventCells.push(tdValue);
                    }
                    return tdValue;
                });

                table.querySelectorAll(`thead th:nth-child(${columnIndex + 1})`).forEach((th) => {
                    let thValue = th as HTMLElement;
                    thValue.style.zIndex = "502";
                    return thValue;
                });

                rightMobile += 50;
                rightTab += 100;
                right += tHead.offsetWidth;
            }
        },

        async initiateStyling() {
            if (this.enabled && !this.isLoading) {
                await this.$nextTick();
                this.addStickyStyle();
            }
        },

        scrollEvent(e) {
            this.$nextTick(() => {
                const el = e.target as HTMLElement;
                const scrollX = el.scrollLeft;
                if (this.lastScrollX !== scrollX) {
                    const tdValue = this.wrapper.querySelector("table td.markDecoy") as HTMLElement;
                    if (!tdValue) return;
                    tdValue.classList.add("sticky");
                    tdValue.click();
                    tdValue.classList.remove("markDecoy");
                }
            });
        },

        hiddenDropDownEvent(e) {
            const tdValue = e.currentTarget as HTMLElement;
            tdValue.classList.add("sticky");
            tdValue.classList.remove("markDecoy");
        },

        shownDropDownEvent(e) {
            const tdValue = e.currentTarget as HTMLElement;
            tdValue.classList.remove("sticky");
            tdValue.classList.add("markDecoy");
            requestAnimationFrame(() => {
                this.wrapper.scrollLeft = this.wrapper.scrollWidth;
                this.lastScrollX = this.wrapper.scrollLeft;
            });
        },

        calculateColspan() {
            if (!this.wrapper) this.wrapper = this.$refs.stickyWrapper as HTMLDivElement;

            const table = this.wrapper.querySelector("table");
            const containerWidth = this.wrapper.clientWidth;
            const tableHead = table.querySelectorAll("thead th");
            if (!tableHead) return;

            let totalWidth = 0;
            let span = 0;

            tableHead.forEach((cell) => {
                let cellValue = cell as HTMLElement;
                let cellWidth = cellValue.offsetWidth;
                if (totalWidth + cellWidth > containerWidth) return;
                totalWidth += cellWidth;
                span++;
            });

            this.newColspan = Math.max(span, 1);
            if (this.newColspan !== this.columns) {
                this.$emit("update:columns", this.newColspan);
            }
        }
    }
});
